import { useCallback, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
  type Params,
} from 'react-router-dom';
import {
  DEFAULT_WORKSPACE_ID,
  isWorkspaceId,
  useWorkspace,
  type WorkspaceId,
} from './WorkspaceContext';

export function workspacePath(workspaceId: WorkspaceId, path = ''): string {
  const normalized = !path || path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;
  return `/app/${workspaceId}${normalized}`;
}

export function useWorkspacePath(): (path?: string) => string {
  const { workspaceId } = useWorkspace();
  return useCallback((path = '') => workspacePath(workspaceId, path), [workspaceId]);
}

export function WorkspaceRoute() {
  const { workspaceId: routeWorkspaceId } = useParams();
  const location = useLocation();
  const { workspaceId, setWorkspaceId } = useWorkspace();
  const routeId = routeWorkspaceId ?? null;
  const validWorkspaceId = isWorkspaceId(routeId) ? routeId : null;

  useEffect(() => {
    if (validWorkspaceId && validWorkspaceId !== workspaceId) setWorkspaceId(validWorkspaceId);
  }, [setWorkspaceId, validWorkspaceId, workspaceId]);

  if (!validWorkspaceId) {
    const rest = location.pathname.replace(/^\/app\/[^/]+/, '') || '';
    return <Navigate to={`${workspacePath(DEFAULT_WORKSPACE_ID, rest)}${location.search}${location.hash}`} replace />;
  }

  if (validWorkspaceId !== workspaceId) {
    return null;
  }

  return <Outlet />;
}

export function WorkspaceHomeRedirect() {
  const { workspaceId } = useWorkspace();
  return <Navigate to={workspacePath(workspaceId)} replace />;
}

function redirectSearch(locationSearch: string, searchParams?: Record<string, string>): string {
  const params = new URLSearchParams(locationSearch);
  for (const [key, value] of Object.entries(searchParams ?? {})) params.set(key, value);
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function WorkspacePathRedirect({
  to,
  searchParams,
}: {
  to: string;
  searchParams?: Record<string, string>;
}) {
  const location = useLocation();
  const { workspaceId } = useWorkspace();
  return (
    <Navigate
      to={`${workspacePath(workspaceId, to)}${redirectSearch(location.search, searchParams)}${location.hash}`}
      replace
    />
  );
}

export function LegacyWorkspaceRedirect({
  to,
  searchParams,
}: {
  to: string | ((params: Readonly<Params<string>>) => string);
  searchParams?: Record<string, string>;
}) {
  const params = useParams();
  const location = useLocation();
  const targetPath = typeof to === 'function' ? to(params) : to;
  return (
    <Navigate
      to={`${workspacePath(DEFAULT_WORKSPACE_ID, targetPath)}${redirectSearch(location.search, searchParams)}${location.hash}`}
      replace
    />
  );
}
